import { RotateCw } from "lucide-react";
import { useT } from "@/src/i18n";
import {
  MARKER_OBJECT_CATALOG,
  MARKER_OBJECT_CATEGORIES,
  type MarkerObjectDefinition,
} from "@/src/core/engine/markerObjects";
import { useMapStore } from "@/src/store/useMapStore";

/** Miniature preview, matching the map renderer. Mirrors the other two pickers. */
function MarkerGlyph({ definition }: { definition: MarkerObjectDefinition }) {
  const { style } = definition;
  const stroke = definition.dmOnly ? "#d08bce" : "currentColor";
  const parts = [];

  if (style.column === "round") {
    parts.push(<circle key="c" cx="16" cy="12" r="7" stroke={stroke} strokeWidth="1.4" fill="none" />);
  }
  if (style.column === "square") {
    parts.push(<rect key="sq" x="9" y="5" width="14" height="14" stroke={stroke} strokeWidth="1.4" fill="none" />);
    parts.push(<path key="sqd" d="M9 5 L23 19" stroke={stroke} strokeWidth="1" />);
  }
  if (style.figure) {
    parts.push(<rect key="pl" x="8" y="16" width="16" height="4" stroke={stroke} strokeWidth="1.2" fill="none" />);
    parts.push(<circle key="hd" cx="16" cy="6" r="2.4" stroke={stroke} strokeWidth="1.2" fill="none" />);
    parts.push(<path key="bd" d="M16 8.5 L16 16 M12 11 L20 11" stroke={stroke} strokeWidth="1.2" />);
  }
  if (style.block === "dais") {
    parts.push(<rect key="d1" x="4" y="4" width="24" height="16" stroke={stroke} strokeWidth="1.3" fill="none" />);
    parts.push(<rect key="d2" x="8" y="7" width="16" height="10" stroke={stroke} strokeWidth="1" fill="none" />);
    parts.push(<rect key="d3" x="12" y="10" width="8" height="4" stroke={stroke} strokeWidth="1" fill="none" />);
  }
  if (style.block === "altar") {
    parts.push(<rect key="a" x="5" y="7" width="22" height="10" stroke={stroke} strokeWidth="1.3" fill="none" />);
    parts.push(<path key="a2" d="M8 10 L24 10 M8 14 L24 14" stroke={stroke} strokeWidth="1" />);
  }
  if (style.block === "throne") {
    parts.push(<rect key="t" x="9" y="4" width="14" height="16" stroke={stroke} strokeWidth="1.3" fill="none" />);
    parts.push(<path key="t2" d="M9 4 L9 20 M13 4 L23 4 M13 20 L23 20" stroke={stroke} strokeWidth="1.2" />);
  }
  if (style.flame) {
    parts.push(<circle key="b" cx="16" cy="12" r="7" stroke={stroke} strokeWidth="1.3" fill="none" />);
    parts.push(<path key="f" d="M16 16 Q20 12 16 7 Q12 12 16 16" stroke={stroke} strokeWidth="1.2" fill="none" />);
  }
  if (style.chest) {
    const band = style.chest === "gold" ? "#d5a72f" : style.chest === "iron" ? "#9aa3a5" : "#a8763f";
    parts.push(<rect key="ch" x="7" y="7" width="18" height="10" stroke={stroke} strokeWidth="1.3" fill={band} fillOpacity="0.5" />);
    parts.push(<path key="ch2" d="M7 11 L25 11" stroke={stroke} strokeWidth="1.2" />);
    parts.push(<circle key="ch3" cx="16" cy="14" r="1.3" fill={stroke} />);
  }
  if (style.glyph) {
    parts.push(<circle key="g" cx="16" cy="12" r="8" stroke={stroke} strokeWidth="1.3" fill="none" />);
    parts.push(
      <text key="gt" x="16" y="12" textAnchor="middle" dominantBaseline="central"
        fill={stroke} fontSize="11" fontWeight="700" fontFamily="Georgia, serif">
        {style.glyph}
      </text>,
    );
  }

  return <svg viewBox="0 0 32 24" className="h-6 w-8" aria-hidden="true" fill="none">{parts}</svg>;
}

export function MarkerTypePicker() {
  const selected = useMapStore((state) => state.selectedMarkerType);
  const setSelected = useMapStore((state) => state.setSelectedMarkerType);
  const rotation = useMapStore((state) => state.floorPlacementRotation);
  const rotate = useMapStore((state) => state.rotateFloorPlacement);
  const t = useT();

  return (
    <section className="border-t border-white/8 px-3 py-3">
      <p className="mb-1 px-1 text-[9px] font-bold uppercase tracking-[0.18em] text-stone-600">
        {t("marker.pickerTitle")}
      </p>
      <p className="mb-2 px-1 text-[9px] leading-4 text-stone-600">{t("marker.pickerHint")}</p>

      <button
        type="button"
        onClick={() => rotate()}
        data-testid="marker-rotate"
        className="mb-2 flex h-8 w-full items-center justify-center gap-2 rounded-lg border border-white/8 bg-white/[0.03] text-[10px] font-semibold text-stone-300 transition hover:border-white/20 hover:text-white"
      >
        <RotateCw size={12} /> {t("floor.rotation", { degrees: rotation })}
      </button>

      {MARKER_OBJECT_CATEGORIES.map((category) => {
        const entries = MARKER_OBJECT_CATALOG.filter((entry) => entry.category === category);
        if (entries.length === 0) return null;
        return (
          <div key={category} className="mb-2 last:mb-0">
            <p className="mb-1 px-1 text-[8px] font-bold uppercase tracking-[0.16em] text-stone-700">
              {t(`markerCategory.${category}`)}
            </p>
            <div className="grid grid-cols-3 gap-1.5">
              {entries.map((entry) => {
                const active = selected === entry.id;
                return (
                  <button
                    key={entry.id}
                    type="button"
                    onClick={() => setSelected(entry.id)}
                    aria-pressed={active}
                    title={`${t(entry.labelKey)}${entry.dmOnly ? ` · ${t("wall.dmOnly")}` : ""}`}
                    data-markertype={entry.id}
                    className={`relative flex min-h-14 flex-col items-center justify-center gap-1 rounded-xl border p-1 text-center transition ${active ? "border-[#c3463d] bg-[#a8322c]/25 text-white" : "border-white/6 bg-white/[0.025] text-stone-400 hover:border-white/15 hover:bg-white/[0.06] hover:text-stone-100"}`}
                  >
                    <MarkerGlyph definition={entry} />
                    <span className="text-[8px] font-medium leading-none">{t(entry.labelKey)}</span>
                    {entry.dmOnly && (
                      <span className="absolute end-1 top-1 h-1.5 w-1.5 rounded-full bg-[#d08bce]"
                        title={t("wall.dmOnlyHint")} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </section>
  );
}
